import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HardHat, Loader2, Receipt } from "lucide-react";
import { AltriCosti } from "@/components/AltriCosti";

const fmt = (n) => (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const importo = (p) => Number(p.paga_totale) || ((Number(p.ore_ordinarie) || 0) * (Number(p.tariffa_ordinaria) || 0)
  + (Number(p.ore_straordinarie) || 0) * (Number(p.tariffa_straordinaria) || 0));

export const CostiCantiere = ({ cantiereId, onChanged }) => {
  const [giornate, setGiornate] = useState(null);
  const [nomi, setNomi] = useState({});
  const [costi, setCosti] = useState([]);
  const loadCosti = () => api.get(`/cantieri/${cantiereId}/costi`).then(({ data }) => setCosti(data)).catch(() => setCosti([]));

  useEffect(() => {
    loadCosti();
    Promise.all([api.get("/presenze", { params: { cantiere_id: cantiereId } }), api.get("/users").catch(() => ({ data: [] }))])
      .then(([p, u]) => {
        setNomi(Object.fromEntries((u.data || []).map((x) => [x.id, x.name || x.email])));
        setGiornate((p.data || []).filter((x) => (x.tipo || "presenza") === "presenza")
          .sort((a, b) => String(b.data || "").localeCompare(String(a.data || ""))));
      })
      .catch(() => setGiornate([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantiereId]);

  if (giornate === null) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  const totale = giornate.reduce((s, g) => s + importo(g), 0);

  return (
    <Tabs defaultValue="manodopera" data-testid="costi-cantiere">
      <TabsList>
        <TabsTrigger value="manodopera" data-testid="costi-tab-manodopera"><HardHat className="h-4 w-4 mr-2" />Manodopera ({giornate.length})</TabsTrigger>
        <TabsTrigger value="altri" data-testid="costi-tab-altri"><Receipt className="h-4 w-4 mr-2" />Altri Costi ({costi.length})</TabsTrigger>
      </TabsList>
      <TabsContent value="manodopera" className="space-y-4 pt-4">
        <Card className="card-elevated"><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale costo manodopera</div>
          <div className="text-2xl font-extrabold text-primary" data-testid="costi-manodopera-totale">{fmt(totale)}</div>
        </CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="costi-manodopera-table">
            <thead className="bg-secondary">
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3">Data</th><th className="px-4 py-3">Dipendente</th>
                <th className="px-4 py-3">Ore / Giornata</th><th className="px-4 py-3 text-right">Importo</th>
              </tr>
            </thead>
            <tbody>
              {giornate.map((g) => (
                <tr key={g.id} className="border-t border-border hover:bg-secondary/40" data-testid={`costi-giornata-${g.id}`}>
                  <td className="px-4 py-3 tabular-nums">{g.data}</td>
                  <td className="px-4 py-3 font-medium">{g.custom_nome || nomi[g.operaio_id] || g.operaio_nome || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">
                    {Number(g.ore_ordinarie) || 0} h{Number(g.ore_straordinarie) > 0 && <span className="text-amber-700"> + {g.ore_straordinarie} h straord.</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{fmt(importo(g))}</td>
                </tr>
              ))}
              {giornate.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Nessuna giornata registrata per questo cantiere.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent></Card>
      </TabsContent>
      <TabsContent value="altri" className="pt-4">
        <AltriCosti cantiereId={cantiereId} costi={costi} onChanged={() => { loadCosti(); onChanged?.(); }} />
      </TabsContent>
    </Tabs>
  );
};
